/**
 * Service for managing saved cars via the backend API.
 * Replaces localStorage-based saved cars with database persistence.
 */

export interface SavedCarPublic {
    id: number;
    clerk_user_id: string;
    car_id: string;
    saved_at: string;
}

export interface SavedCarWithDetails {
    id: number;
    car_id: string;
    saved_at: string;
    car: CarListing | null;
}

// CarListing interface matching the backend AutoUsadoPublic model
export interface CarListing {
    id_unico: string;
    url?: string;
    imagen_url?: string;
    image_url_2?: string;
    image_url_3?: string;
    image_url_4?: string;
    image_url_5?: string;
    vendedor_nombre?: string;
    vendedor_telefono?: string;
    marca: string;
    modelo: string;
    fecha_fabricacion: number;
    kilometraje: number;
    transmision: string;
    tipo_combustible: string;
    tipo_traccion?: string;
    motor?: string;
    fecha_publicacion: string;
    departamento?: string;
    tipo_vendedor?: string;
    color?: string;
    version?: string;
    unico_dueno?: string;
    negociable?: string;
    financiamiento?: string;
    asientos_cuero?: string;
    seguridad?: string;
    opciones?: string;
    version_grupo?: string;
    categoria?: string;
    tipo_cabina?: string;
    mantenimiento_status?: string;
    acepta_cambios?: string;
    precio: number;
    descripcion?: string;
    puntuacion?: number;
    popularidad?: number;
    views_count?: number;
    comentario_experto?: string;
    image_analysis?: string;
    precio_mercado_maximo?: number;
    precio_mercado_minimo?: number;
    precio_mercado_promedio?: number;
    plataforma?: string;
    transparencia?: number;
    transparencia_explicacion?: string;
    valor?: number;
    rebaja?: number;
    mantenimiento?: string;
    mantenimiento_detalle?: string;
    cambio?: string;
    financiamiento_detalle?: string;
    vendor_stats?: {
        vendedor_id: string;
        nombre_vendedor: string;
        total_autos_vendidos?: number;
        precio_promedio?: number;
        marcas_vendidas?: string[];
        ubicacion?: string[];
        fecha_primer_listado?: string;
        fecha_ultimo_listado?: string;
    };
    // Added for compatibility with existing UI components
    savedAt?: string;
}

class SavedCarsService {
    private userId: string | null = null;

    setUserId(userId: string) {
        this.userId = userId;
    }

    private getHeaders(): HeadersInit {
        const headers: HeadersInit = {
            "Content-Type": "application/json",
        };
        if (this.userId) {
            headers["x-clerk-user-id"] = this.userId;
        }
        return headers;
    }

    /**
     * Get all saved cars for the current user with full car details.
     */
    async listSavedCars(): Promise<SavedCarWithDetails[]> {
        const response = await fetch("/api/saved-cars", {
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch saved cars: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Get the count of saved cars for the current user.
     */
    async getSavedCarsCount(): Promise<number> {
        const response = await fetch("/api/saved-cars/count", {
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to get saved cars count: ${response.status}`);
        }

        const data = await response.json();
        return data.count;
    }

    /**
     * Get all saved car IDs for the current user (lightweight for UI indicators).
     */
    async getSavedCarIds(): Promise<string[]> {
        const response = await fetch("/api/saved-cars/ids", {
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to get saved car IDs: ${response.status}`);
        }

        const data = await response.json();
        return data.car_ids;
    }

    /**
     * Save a car for the current user.
     */
    async saveCar(carId: string): Promise<SavedCarPublic> {
        const response = await fetch("/api/saved-cars", {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify({ car_id: carId }),
        });

        if (!response.ok) {
            throw new Error(`Failed to save car: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Remove a car from the user's saved list.
     */
    async unsaveCar(carId: string): Promise<void> {
        const response = await fetch(`/api/saved-cars/${encodeURIComponent(carId)}`, {
            method: "DELETE",
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to unsave car: ${response.status}`);
        }
    }

    /**
     * Check if a specific car is saved by the current user.
     */
    async isCarSaved(carId: string): Promise<boolean> {
        const response = await fetch(`/api/saved-cars/check/${encodeURIComponent(carId)}`, {
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to check saved status: ${response.status}`);
        }

        const data = await response.json();
        return data.is_saved;
    }

    /**
     * Toggle the saved status of a car.
     * Returns true if car is now saved, false if unsaved.
     */
    async toggleSaved(carId: string): Promise<boolean> {
        const isSaved = await this.isCarSaved(carId);

        if (isSaved) {
            await this.unsaveCar(carId);
            return false;
        } else {
            await this.saveCar(carId);
            return true;
        }
    }
}

export const savedCarsService = new SavedCarsService();
